import { useEffect } from 'react';
import { setMainHeading } from '../../../redux/modules/mainHeading/slice';
import { useDispatch } from 'react-redux';
import { usePermissions } from '../../../hooks/usePermissions';
import { Box, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const SettingsCard = styled(Link)(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px 24px',
  borderRadius: 16,
  textDecoration: 'none',
  backgroundColor: active ? theme.palette.primary.main : theme.palette.background.paper,
  color: active ? '#fff' : theme.palette.text.primary,
  border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
  transition: 'all 200ms ease',
  boxShadow: active ? '0 8px 16px rgba(105, 203, 249, 0.3)' : 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: active
      ? '0 12px 24px rgba(105, 203, 249, 0.4)'
      : '0 8px 16px rgba(0, 0, 0, 0.08)',
    borderColor: theme.palette.primary.main,
    backgroundColor: active ? theme.palette.primary.main : 'rgba(105, 203, 249, 0.05)',
  },
}));

const IconWrapper = styled(Box)(({ theme, active }) => ({
  width: 48,
  height: 48,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: active ? 'rgba(255, 255, 255, 0.2)' : theme.palette.primary.light,
  color: active ? '#fff' : theme.palette.primary.main,
  marginRight: 16,
}));

// Each card is gated by its own capability (mirrors the server guards).
// Branches + Audit Log are grantable; Permissions (role management) stays
// admin-only to avoid self-escalation.
const settingsItems = [
  {
    id: 'branches',
    title: 'Branches',
    description: 'Manage clinic locations and branches',
    path: 'branches',
    icon: BusinessIcon,
    gate: 'branches:write',
  },
  {
    id: 'permissions',
    title: 'Permissions',
    description: 'Control what each role can access',
    path: 'permissions',
    icon: SecurityOutlinedIcon,
    gate: 'admin',
  },
  {
    id: 'audit-log',
    title: 'Audit Log',
    description: 'Review staff activity on patients and their records',
    path: 'audit-log',
    icon: FactCheckOutlinedIcon,
    gate: 'auditLogs:read',
  },
];

export default function Settings() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { can, isAdmin } = usePermissions();
  const visibleItems = settingsItems.filter(item => item.gate === 'admin' ? isAdmin : can(item.gate));

  useEffect(() => {
    dispatch(setMainHeading("Settings"));
  }, [dispatch]);

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 4 }}
      >
        Configure your clinic settings and preferences
      </Typography>

      <Grid container spacing={2}>
        {visibleItems.map((item, index) => {
          const Icon = item.icon;
          const isActive = location.pathname.includes(item.path);

          return (
            <Grid item xs={12} key={item.id}>
              <SettingsCard to={item.path} active={isActive ? 1 : 0}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconWrapper active={isActive ? 1 : 0}>
                    <Icon />
                  </IconWrapper>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: '1.0625rem',
                        color: 'inherit',
                        mb: 0.25,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isActive ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                        fontSize: '0.8125rem',
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Box>
                <ChevronRightIcon
                  sx={{
                    color: isActive ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                    fontSize: 24,
                  }}
                />
              </SettingsCard>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
